import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, TrendingUp, Clock, Activity } from "lucide-react";
import ClientOnly from "@/components/map/ClientOnly";
import "@/components/map/leaflet-defaults";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";

interface Vehicle {
  id: string;
  name: string;
  status: 'moving' | 'stopped' | 'idle';
  speed: number;
  lat: number;
  lng: number;
}

const mockVehicles: Vehicle[] = [
  { id: '1', name: 'Caminhão A-123', status: 'moving', speed: 65, lat: -23.550520, lng: -46.633308 },
  { id: '2', name: 'Van B-456', status: 'stopped', speed: 0, lat: -23.561684, lng: -46.656139 },
  { id: '3', name: 'Utilitário C-789', status: 'moving', speed: 42, lat: -23.563987, lng: -46.654321 },
  { id: '4', name: 'Caminhão D-012', status: 'idle', speed: 5, lat: -23.547683, lng: -46.641256 },
];

export function TrackingLiveDemo() {
  const [vehicles, setVehicles] = useState<Vehicle[]>(mockVehicles);
  const [selectedVehicle, setSelectedVehicle] = useState<Vehicle | null>(mockVehicles[0]);

  // Simulate vehicle movement
  useEffect(() => {
    const interval = setInterval(() => {
      setVehicles(prev => prev.map(vehicle => {
        if (vehicle.status === 'moving') {
          return {
            ...vehicle,
            lat: vehicle.lat + (Math.random() - 0.5) * 0.001,
            lng: vehicle.lng + (Math.random() - 0.5) * 0.001,
            speed: Math.max(30, Math.min(80, vehicle.speed + (Math.random() - 0.5) * 10))
          };
        }
        return vehicle;
      }));
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'moving': return 'bg-green-500';
      case 'stopped': return 'bg-red-500';
      case 'idle': return 'bg-yellow-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'moving': return 'Em movimento';
      case 'stopped': return 'Parado';
      case 'idle': return 'Ocioso';
      default: return 'Desconhecido';
    }
  };

  const createVehicleIcon = (status: string) => {
    const color = status === 'moving' ? '#10b981' : status === 'stopped' ? '#ef4444' : '#f59e0b';
    const pulseClass = status === 'moving' ? 'animate-ping' : '';
    
    return L.divIcon({
      className: 'custom-vehicle-marker',
      html: `
        <div class="relative">
          ${status === 'moving' ? `<div class="absolute inset-0 rounded-full bg-green-500 ${pulseClass} opacity-75"></div>` : ''}
          <div class="w-4 h-4 rounded-full border-2 border-white shadow-lg" style="background-color: ${color}"></div>
        </div>
      `,
      iconSize: [16, 16],
      iconAnchor: [8, 8],
      popupAnchor: [0, -8]
    });
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      {/* Map Preview */}
      <div className="lg:col-span-2 relative h-[500px] rounded-lg overflow-hidden border border-border">
        <ClientOnly>
          <MapContainer 
            center={[-23.55, -46.63]} 
            zoom={13} 
            className="h-full w-full"
            zoomControl={true}
          >
            <TileLayer 
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />
            
            {/* Vehicle markers */}
            {vehicles.map((vehicle) => (
              <Marker 
                key={vehicle.id}
                position={[vehicle.lat, vehicle.lng]}
                icon={createVehicleIcon(vehicle.status)}
                eventHandlers={{
                  click: () => setSelectedVehicle(vehicle)
                }}
              >
                <Popup>
                  <div className="p-1 min-w-[180px]">
                    <div className="text-sm font-semibold text-gray-900 mb-2">{vehicle.name}</div>
                    <div className="text-xs text-gray-600 space-y-1">
                      <div className="flex items-center gap-1">
                        <Activity className="w-3 h-3" />
                        {getStatusLabel(vehicle.status)}
                      </div>
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" />
                        {vehicle.speed} km/h
                      </div>
                      <div className="flex items-center gap-1">
                        <MapPin className="w-3 h-3" />
                        {vehicle.lat.toFixed(4)}, {vehicle.lng.toFixed(4)}
                      </div>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </ClientOnly>

        {/* Map controls overlay */}
        <div className="absolute top-4 left-4 z-[1000] space-y-2 pointer-events-none">
          <Badge className="bg-[#0d9488]/90 text-white border-[#0d9488] pointer-events-auto">
            <Activity className="w-3 h-3 mr-1 animate-pulse" />
            Atualização em tempo real
          </Badge>
        </div>

        {/* Legend */}
        <div className="absolute bottom-4 left-4 z-[1000] bg-white/10 backdrop-blur border border-white/20 rounded-lg p-3 pointer-events-none">
          <div className="text-xs text-white/90 font-semibold mb-2">Legenda</div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs text-white/80">
              <div className="w-3 h-3 rounded-full bg-green-500" />
              Em movimento
            </div>
            <div className="flex items-center gap-2 text-xs text-white/80">
              <div className="w-3 h-3 rounded-full bg-yellow-500" />
              Ocioso
            </div>
            <div className="flex items-center gap-2 text-xs text-white/80">
              <div className="w-3 h-3 rounded-full bg-red-500" />
              Parado
            </div>
          </div>
        </div>
      </div>

      {/* Vehicle List */}
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-white">Veículos Online</h3>
        <div className="space-y-2">
          {vehicles.map((vehicle) => (
            <Card
              key={vehicle.id}
              className={`p-4 cursor-pointer transition-all ${
                selectedVehicle?.id === vehicle.id
                  ? 'bg-[#0d9488]/20 border-[#0d9488]'
                  : 'bg-white/5 border-white/10 hover:bg-white/10'
              }`}
              onClick={() => setSelectedVehicle(vehicle)}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${getStatusColor(vehicle.status)}`} />
                  <span className="text-white font-medium text-sm">{vehicle.name}</span>
                </div>
                <Badge variant="outline" className="text-xs border-white/20 text-white/70">
                  {getStatusLabel(vehicle.status)}
                </Badge>
              </div>
              
              <div className="space-y-1 text-xs text-white/70">
                <div className="flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Velocidade: {vehicle.speed} km/h
                </div>
                <div className="flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  Lat: {vehicle.lat.toFixed(4)}, Lng: {vehicle.lng.toFixed(4)}
                </div>
                <div className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Atualizado há poucos segundos
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Card className="p-4 bg-gradient-to-br from-[#0d9488]/20 to-[#0f766e]/20 border-[#0d9488]/30">
          <div className="text-center text-white/90">
            <div className="text-2xl font-bold text-[#a3e635] mb-1">{vehicles.length}</div>
            <div className="text-xs text-white/70">Veículos conectados</div>
          </div>
        </Card>
      </div>
    </div>
  );
}
